"use client";

import { memo, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Search, Star, ArrowDownUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Define the sorting options
export const sortOptions = [
  { value: "name-asc", label: "Name (A-Z)" },
  { value: "name-desc", label: "Name (Z-A)" },
  { value: "library-asc", label: "Library (A-Z)" },
  // Add more as needed, e.g., 'date-added-desc'
] as const; // Use const assertion for stricter typing

export type SortOptionValue = (typeof sortOptions)[number]["value"];

interface FilterControlsProps {
  isScrolled: boolean;
  searchTerm: string;
  sortOption: SortOptionValue;
  showStarredOnly: boolean;
  starredItemsCount: number;
  onSearchChange: (value: string) => void;
  onSortChange: (value: SortOptionValue) => void;
  onToggleStarredOnly: (checked: boolean) => void;
}

const FilterControlsComponent = ({
  isScrolled,
  searchTerm,
  sortOption,
  showStarredOnly,
  starredItemsCount,
  onSearchChange,
  onSortChange,
  onToggleStarredOnly,
}: FilterControlsProps) => {
  // Prevent hydration mismatch for starred count badge
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <div
      className={cn(
        "flex items-center gap-2 bg-background/60 backdrop-blur-sm border rounded-full p-1 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]",
        isScrolled ? "scale-90 origin-right h-8" : "h-9" // Compact height when scrolled
      )}
      style={{ transitionDuration: "400ms" }}
    >
      {/* Search Input */}
      <div className="relative flex items-center flex-grow md:flex-grow-0">
        <Search
          className={cn(
            "absolute left-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none",
            isScrolled ? "left-2" : "left-2.5"
          )}
        />
        <Input
          type="search"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className={cn(
            "pl-8 pr-2 py-1 rounded-full border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-auto text-xs",
            isScrolled ? "h-6 w-28" : "h-7 w-32 md:w-40" // Smaller input when scrolled
          )}
          style={{ boxShadow: "none" }}
          aria-label="Search components"
        />
      </div>

      {/* Divider */}
      <div
        className={cn(
          "h-4 w-px bg-border mx-0.5",
          isScrolled ? "mx-0" : "mx-0.5"
        )}
      />

      {/* Starred Toggle */}
      <div className="flex items-center gap-1.5 pr-1 relative">
        <Label
          htmlFor="starred-toggle"
          className={cn(
            "flex items-center cursor-pointer transition-colors hover:text-primary",
            showStarredOnly ? "text-primary" : "text-muted-foreground"
          )}
          title={
            showStarredOnly
              ? "Showing starred components"
              : "Show only starred components"
          }
        >
          <Star
            className={cn("h-4 w-4", isScrolled ? "h-3.5 w-3.5" : "h-4 w-4")}
          />
          {/* Badge for starred count */}
          {isMounted && starredItemsCount > 0 && (
            <Badge
              variant={showStarredOnly ? "default" : "secondary"}
              className={cn(
                "absolute -top-1.5 -right-0.5 h-4 min-w-[1rem] p-0.5 flex items-center justify-center text-[9px] leading-none rounded-full pointer-events-none transition-all",
                isScrolled ? "scale-80 -top-1 -right-0" : ""
              )}
              suppressHydrationWarning // Suppress warning for client-side count
            >
              {starredItemsCount}
            </Badge>
          )}
        </Label>
        <Switch
          id="starred-toggle"
          checked={showStarredOnly}
          onCheckedChange={onToggleStarredOnly}
          className={cn(
            "data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
            isScrolled
              ? "h-3 w-6 [&>span]:h-2 [&>span]:w-2"
              : "h-4 w-7 [&>span]:h-3 [&>span]:w-3" // Smaller switch when scrolled
          )}
          aria-label="Toggle starred components"
        />
      </div>

      {/* Divider */}
      <div
        className={cn(
          "h-4 w-px bg-border mx-0.5",
          isScrolled ? "mx-0" : "mx-0.5"
        )}
      />

      {/* Sort Select */}
      <div className="flex items-center">
        <Select
          value={sortOption}
          onValueChange={(value) => onSortChange(value as SortOptionValue)}
        >
          <SelectTrigger
            className={cn(
              "text-xs border-none bg-transparent focus:ring-0 focus:ring-offset-0 h-auto px-2 py-1 gap-1",
              isScrolled ? "h-6 px-1.5" : "h-7 px-2" // Compact trigger
            )}
            aria-label="Sort components by"
          >
            <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
            <span className={cn(isScrolled ? "hidden" : "inline")}>
              <SelectValue placeholder="Sort by..." />
            </span>
          </SelectTrigger>
          <SelectContent align="end">
            {sortOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const FilterControls = memo(FilterControlsComponent);
